import { Injectable, inject } from '@angular/core';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';
import { Indicator } from '../../models/indicator';

export interface DuplicateIndicator {
  indicator: Indicator & { _id: string };
  key: string;
  dataCount: number;
  duplicateCount: number;
}

export interface ValidationResult {
  duplicateKeys: string[];
  indicatorsWithoutData: DuplicateIndicator[];
  totalDuplicates: number;
}

@Injectable({
  providedIn: 'root',
})
export class IndicatorValidatorService {
  private indicatorStore = inject(IndicatorStore);
  private indicatorDataStore = inject(IndicatorDataStore);

  /**
   * Validates indicators for duplicates by key
   * Returns indicators that have the same key and no data
   */
  validateIndicators(): ValidationResult {
    const indicators = Object.values(this.indicatorStore.items());

    // Group indicators by key
    const indicatorsByKey = this.groupIndicatorsByKey(indicators);

    // Find keys with duplicates
    const duplicateKeys = Object.keys(indicatorsByKey).filter(
      key => indicatorsByKey[key].length > 1
    );

    // Find indicators without data that have duplicate keys
    const indicatorsWithoutData: DuplicateIndicator[] = [];

    duplicateKeys.forEach(key => {
      const duplicateIndicators = indicatorsByKey[key];

      duplicateIndicators.forEach(indicator => {
        const dataCount =
          this.indicatorDataStore.countByIndicator(indicator._id) || 0;

        indicatorsWithoutData.push({
          indicator,
          key,
          dataCount,
          duplicateCount: duplicateIndicators.length,
        });
      });
    });

    return {
      duplicateKeys,
      indicatorsWithoutData,
      totalDuplicates: duplicateKeys.length,
    };
  }

  /**
   * Groups a list of indicators by their key property.
   *
   * @param {Indicator[]} indicators - An array of Indicator objects to be grouped.
   * @return {{ [key: string]: Indicator[] }} An object where each key is a unique key from the indicators, and the value is an array of indicators with that key.
   */
  private groupIndicatorsByKey(indicators: Indicator[]): {
    [key: string]: Indicator[];
  } {
    const grouped: { [key: string]: Indicator[] } = {};

    indicators.forEach(indicator => {
      if (!indicator.key) return;

      if (!grouped[indicator.key]) {
        grouped[indicator.key] = [];
      }
      grouped[indicator.key].push(indicator);
    });

    return grouped;
  }
}
